import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { queryClient } from '@/lib/queryClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Process referral on signup
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            processReferralIfExists(session.user.id);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Process referral code if user just signed up with one
  const processReferralIfExists = async (userId: string) => {
    const referralCode = localStorage.getItem('referral_code');
    if (!referralCode) return;

    try {
      const response = await supabase.functions.invoke('process-referral', {
        body: { referral_code: referralCode, new_user_id: userId },
      });

      if (response.data?.success) {
        console.log('Referral processed successfully:', response.data);
        localStorage.removeItem('referral_code');
      }
    } catch (error) {
      console.error('Error processing referral:', error);
    }
  };

  const signOut = async () => {
    // Clear sensitive local data on logout
    try {
      localStorage.removeItem('offline_queue');
      localStorage.removeItem('timer_settings');
      localStorage.removeItem('jedi-query-cache');
    } catch (e) {
      console.error('Error clearing local storage:', e);
    }
    queryClient.clear();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
